import { FilePlus2, FolderOpen, HardDrive } from 'lucide-react';
import { useState } from 'react';
import { useAppStore } from '../store/useAppStore';
import { Button, Input } from './ui';

/**
 * What you get before there is a plan to look at.
 *
 * The app used to invent "Scenario 1" the moment anybody opened it, so that a
 * change always had somewhere to land. That is a worse trade than it looks:
 * opening the app is not the same as wanting a plan, and once scenarios live in
 * Drive it meant a stray file appearing in somebody's own Drive for having
 * looked at the org chart once. Asking costs one click and invents nothing.
 */
export function FirstRun() {
  const scenarios = useAppStore((s) => s.scenarioList);
  const newScenario = useAppStore((s) => s.newScenario);
  const selectScenario = useAppStore((s) => s.selectScenario);
  const openFromDrive = useAppStore((s) => s.openFromDrive);
  // Both: without the project number a pick grants this app nothing, so the
  // button would open a dialog that cannot deliver what it offers.
  const canPick = useAppStore((s) => s.pickerApiKey !== null && s.pickerAppId !== null);

  const [naming, setNaming] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const create = () => {
    const name = draft.trim();
    if (!name || busy) return;
    setBusy(true);
    void newScenario(name).finally(() => setBusy(false));
  };

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-[var(--color-canvas)] p-6">
      <div className="w-full max-w-md rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface)] p-8 shadow-[var(--shadow-panel)]">
        <h2 className="font-display text-[17px] font-semibold text-[var(--color-ink)]">
          Nothing planned yet
        </h2>
        <p className="mt-2 text-[13px] text-[var(--color-ink-3)]">
          A scenario is where changes live — moves, new roles, departures — layered on top of today's org,
          which is never touched. It is one file in your own Drive, and only yours until you share it.
        </p>

        {naming ? (
          <div className="mt-6 flex gap-2">
            <Input
              autoFocus
              aria-label="Scenario name"
              placeholder="Q4 reorg"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') create();
                if (event.key === 'Escape') setNaming(false);
              }}
            />
            <Button variant="primary" onClick={create} disabled={!draft.trim() || busy}>
              Create
            </Button>
          </div>
        ) : (
          <Button variant="primary" className="mt-6 w-full" onClick={() => setNaming(true)}>
            <FilePlus2 size={15} />
            Start a scenario
          </Button>
        )}

        {/*
          A plan somebody shared is not in the list until it has been picked —
          `drive.file` cannot see a file the person has not chosen — so this is
          the way in to one, and the first thing somebody sent a link needs.
        */}
        {canPick ? (
          <>
            <Button variant="ghost" className="mt-2 w-full" onClick={() => void openFromDrive()}>
              <HardDrive size={15} />
              Open one from Drive
            </Button>
            {/*
              Said before the dialog rather than after it, because Google's picker
              reports this as "the API developer key is invalid" — a message about
              something nobody here can fix, for a cause nobody would guess. It
              needs third-party cookies, and a private window blocks them by
              default. The failure is inside Google's own iframe, so there is
              nothing to catch and turn into a better sentence afterwards.
            */}
            <p className="mt-1.5 text-[11px] leading-snug text-[var(--color-ink-3)]">
              Google’s picker needs third-party cookies. In a private window, allow them first.
            </p>
          </>
        ) : null}

        {/*
          Anything already in this person`s own Drive is listed by the server, so
          this half is a choice rather than a dialog.
        */}
        {scenarios.length > 0 ? (
          <div className="mt-6 border-t border-[var(--color-line)] pt-4">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
              <FolderOpen size={12} />
              Or open one you already have
            </p>
            <ul className="mt-2 space-y-0.5">
              {scenarios.map((summary) => (
                <li key={summary.id}>
                  <button
                    type="button"
                    onClick={() => void selectScenario(summary.id)}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left text-[13px] text-[var(--color-ink-2)] hover:bg-[var(--color-surface-2)]"
                  >
                    <span className="truncate">{summary.name}</span>
                    <span className="shrink-0 text-[11px] text-[var(--color-ink-3)]">
                      {summary.changeCount} change{summary.changeCount === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
